import {
    EditableEntity,
    IEditableEntity,
    IPropEventBroker,
    IReadablePropEventBroker,
} from '@zdrbm/zdr-native-tools';
import { IDrawerController } from '../drawer/DrawerController';
import { CaptureScreenResult, IScreenshotService, ScreenshotStatus } from '../../../../infra';

export enum PanelTypes {
    History = 'History',
    Conversation = 'Conversation',
    NewConversation = 'NewConversation',
}

export const DEFAULT_DRAWER_SIZE = 480;

export interface IAssistantUiManager extends IEditableEntity {
    toggleHistory(): void;
    createNewChat(): void;
    openConversation(conversationId: string): void;
    closeAssistantPanel(): void;
    currentPanel: IReadablePropEventBroker<PanelTypes>;
    supportsFeedback: IReadablePropEventBroker<boolean>;
    currentConversation: IReadablePropEventBroker<string | undefined>;
    getDrawerSize(): number;
    capturePIScreenshot(): Promise<CaptureScreenResult>;
}

export interface IAssistantFeatureFlags {
    supportFeedback?: boolean;
}

export class AssistantUiManager
    extends EditableEntity<keyof IAssistantUiManager>
    implements IAssistantUiManager
{
    currentPanel: IPropEventBroker<PanelTypes>;
    currentConversation: IPropEventBroker<string | undefined>;
    supportsFeedback: IReadablePropEventBroker<boolean, string>;

    constructor(
        private drawerController: IDrawerController,
        private screenshotService: IScreenshotService,
        private flags: IAssistantFeatureFlags,
    ) {
        super(undefined);
        this.currentPanel = this.createPropEventBroker('currentPanel', PanelTypes.NewConversation);
        this.currentConversation = this.createPropEventBroker('currentConversation', undefined);
        this.supportsFeedback = this.createPropEventBroker(
            'supportsFeedback',
            flags.supportFeedback ?? false,
        );
    }

    async capturePIScreenshot(): Promise<CaptureScreenResult> {
        const { status, image } = await this.screenshotService.captureScreen();
        let finalImage = image;
        const isDrawerOpen = this.drawerController.isOpen.get();

        if (status === ScreenshotStatus.SUCCESS && image && isDrawerOpen) {
            const size = await image.getDimensions();
            finalImage = await image.crop(0, 0, size.width - this.getDrawerSize(), size.height);
        }
        return {status, image: finalImage};
    }

    getDrawerSize(): number {
        return DEFAULT_DRAWER_SIZE;
    }

    closeAssistantPanel(): void {
        this.drawerController.closeDrawer();
    }

    openConversation(conversationId: string): void {
        this.currentConversation.set(conversationId);
        this.currentPanel.set(PanelTypes.Conversation);
    }

    toggleHistory(): void {
        const current = this.currentPanel.get();
        const currentConversation = this.currentConversation.get();
        if (current === PanelTypes.History && currentConversation) {
            this.currentPanel.set(PanelTypes.Conversation);
        } else if (current === PanelTypes.History) {
            this.currentPanel.set(PanelTypes.NewConversation);
        } else {
            this.currentPanel.set(PanelTypes.History);
        }
    }

    createNewChat(): void {
        this.currentConversation.set(undefined);
        this.currentPanel.set(PanelTypes.NewConversation);
    }

    getEntityName(): string {
        return 'AssistantUiManager';
    }
}
